import React, { useCallback } from "react";
import { queryCache } from "react-query";
import clsx from "clsx";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import OverflowTooltip from "../../../components/OverflowTooltip/OverflowTooltip";
import { ActionBar } from "../../../components/ActionBar/ActionBar";
import { QUERY_KEYS } from "../../../constants";
import { useWizard } from "../../wizard/context";
import { useAcademicsSecondaryContext } from "../pages/AcademicsPageSecondary";
import { useAcademicStyles } from "../styles/useAcademicStyles";
import { IAcademicListItemSecondary } from "../types";
import { ProgramSecondary } from "./program/ProgramSecondary";
import { Accordion, AccordionDetails, AccordionSummary } from "./Accordion";
import { getBeautifiedCount } from "../../../helpers";

export function AcademicSecondary({
  academic,
  highlighted,
}: {
  academic: IAcademicListItemSecondary;
  highlighted: boolean;
}) {
  const classes = useAcademicStyles();
  const { openWizard } = useWizard();

  const { academicWizardConfigs, programWizardConfigs, refetchAcademics } =
    useAcademicsSecondaryContext();

  const { schoolStudentApplications, degrees } = QUERY_KEYS;

  const createProgram = useCallback(async () => {
    if (await openWizard(programWizardConfigs.getCreateConfig(academic.id))) {
      await refetchAcademics();
      await queryCache.invalidateQueries([degrees]);
    }
  }, [academic.id, openWizard, programWizardConfigs, refetchAcademics]);

  const changeAcademicStatus = useCallback(async () => {
    if (academic.isActive) {
      if (
        await openWizard(academicWizardConfigs.getDeactivateConfig(academic.id))
      ) {
        await refetchAcademics();
        queryCache.invalidateQueries([schoolStudentApplications]);
      }
    } else {
      if (
        await openWizard(academicWizardConfigs.getActivateConfig(academic.id))
      ) {
        await refetchAcademics();
      }
    }
  }, [academic]);

  const viewAcademic = useCallback(
    () => openWizard(academicWizardConfigs.getViewConfig(academic.id)),
    [academic],
  );
  const editAcademic = useCallback(
    () => openWizard(academicWizardConfigs.getEditConfig(academic.id)),
    [academic],
  );

  const deleteAcademic = useCallback(async () => {
    if (await openWizard(academicWizardConfigs.getDeleteConfig(academic.id))) {
      await refetchAcademics();
      await queryCache.invalidateQueries([degrees]);
    }
  }, [academic]);

  return (
    <Accordion>
      <AccordionSummary
        className={clsx(highlighted && classes.highlightedSummary)}
        expandIcon={<ArrowRightIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.infoContainer}>
          <div className={classes.name}>
            <OverflowTooltip text={academic.levelName} />
          </div>
          {academic.grade && (
            <div className={classes.subName}>
              <OverflowTooltip text={academic.grade} />
            </div>
          )}
          <div className={classes.programsCount}>
            {`(${getBeautifiedCount(academic.programsCount, "program")})`}
          </div>
        </div>
        <ActionBar
          createCallBack={createProgram}
          createTooltip="Add program"
          viewCallBack={viewAcademic}
          viewTooltip="View academic"
          editCallBack={editAcademic}
          editTooltip="Edit academic"
          switchCallBack={changeAcademicStatus}
          switchTooltipActive="Deactivate academic"
          switchTooltipNotActive="Activate academic"
          switchState={academic.isActive}
          deleteCallBack={deleteAcademic}
          deleteTooltip="Delete academic"
        />
      </AccordionSummary>
      <AccordionDetails>
        <div
          className={clsx(
            classes.accordionDetails,
            !academic.isActive && classes.disabled,
          )}
        >
          {academic.programs.map((program) => (
            <ProgramSecondary key={program.id} programBack={program} />
          ))}
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
